import {
  Card,
  CardContent,
  CardHeader,
  Theme,
  makeStyles,
} from '@material-ui/core';
import { ReactNode } from 'react';

/**
 * Props used internally by the makeStyles hook to generate dynamic styles.
 * @internal
 */
interface MakeStylesProps {
  centre: boolean;
  disabled: boolean;
  fullSize: boolean;
  hasTitle: boolean;
  light: boolean;
  padding: boolean;
  shadow: boolean;
  transparent: boolean;
}

const useStyles = makeStyles<Theme, MakeStylesProps>((theme) => ({
  content: ({ centre, fullSize, hasTitle, padding }) => {
    let paddingValue: string | number = 0;

    if (padding) {
      paddingValue = hasTitle ? '0 40px' : '24px 40px';
    }

    return {
      alignItems: centre ? 'center' : 'start',
      display: 'flex',
      flex: fullSize ? 1 : '0 1 auto',
      flexDirection: 'column',
      gap: 25,
      minHeight: 0,
      padding: paddingValue,
    };
  },
  contentWrapper: ({ disabled, fullSize, light, transparent }) => {
    let background: string;
    if (transparent) {
      background = 'transparent';
    } else if (light) {
      background = theme.palette.common.backgroundMutedMore;
    } else {
      background = theme.palette.common.backgroundHighlight;
    }

    return {
      background,
      display: fullSize ? 'flex' : 'block',
      flex: fullSize ? 1 : '0 1 auto',
      flexDirection: fullSize ? 'column' : 'initial',
      minHeight: 0,
      opacity: disabled ? 0.38 : 1,
      pointerEvents: disabled ? 'none' : 'initial',
    };
  },
  header: ({ padding }) => ({
    borderBottom: `1px solid ${theme.palette.common.backgroundNotification}`,
    margin: padding ? '0 0 24px 0' : 0,
    padding: '25px 40px',
    '& .MuiCardHeader-avatar': {
      marginRight: 0,
    },
  }),
  iconWrapper: {
    alignItems: 'center',
    display: 'flex',
    height: 27,
    justifyContent: 'center',
    marginRight: 24,
    width: 27,
  },
  root: ({ disabled, fullSize, shadow, transparent }) => {
    let backgroundColor: string;
    if (transparent) {
      backgroundColor = 'transparent';
    } else if (disabled) {
      backgroundColor = theme.palette.common.backgroundDark;
    } else {
      backgroundColor = theme.palette.common.backgroundHighlight;
    }

    return {
      backgroundColor,
      borderRadius: 5,
      boxShadow: shadow
        ? `0 0 8px 0 ${theme.palette.common.backgroundDark}`
        : 'none',
      display: fullSize ? 'flex' : 'block',
      flex: fullSize ? 1 : '0 1 auto',
      flexDirection: fullSize ? 'column' : 'initial',
      minHeight: 0,
      padding: 0,
    };
  },
  title: {
    color: 'inherit',
    fontSize: theme.typography.pxToRem(18),
  },
}));

/**
 * Props for the Panel component.
 */
interface PanelProps {
  /**
   * Whether to center-align the content vertically and horizontally.
   * @default false
   */
  centre?: boolean;

  /**
   * The content to display inside the panel.
   */
  children: ReactNode;

  /**
   * Whether the panel is disabled. When disabled, the panel will have reduced
   * opacity and pointer events will be disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Optional icon to display in the panel header (only shown when title is provided).
   */
  icon?: ReactNode;

  /**
   * Whether to use a lighter background color variant.
   * @default false
   */
  light?: boolean;

  /**
   * Whether to apply padding to the content area.
   * @default true
   */
  padding?: boolean;

  /**
   * Whether the panel and content should take up the full available space
   * (width and height).
   * @default false
   */
  fullSize?: boolean;

  /**
   * Whether to apply a box shadow to the panel.
   * @default false
   */
  shadow?: boolean;

  /**
   * Whether to make the panel background transparent (no background color).
   * @default false
   */
  transparent?: boolean;

  /**
   * Optional title to display in the panel header.
   */
  title?: string;
}

/**
 * A reusable panel component built on Material-UI's Card component.
 *
 * The Panel component provides a consistent container for displaying content
 * with optional header, icon, and various styling options. It supports disabled
 * states, different background variants, and customizable padding and shadows.
 *
 * @example
 * ```tsx
 * <Panel title="My Panel" icon={<Icon />} padding shadow>
 *   <div>Panel content goes here</div>
 * </Panel>
 * ```
 *
 * @example
 * ```tsx
 * <Panel title="Disabled Panel" disabled>
 *   This panel is disabled
 * </Panel>
 * ```
 *
 * @param props - The panel props
 * @returns A Material-UI Card component with custom styling
 */
function Panel({
  centre = false,
  children,
  disabled = false,
  fullSize = false,
  icon,
  light = false,
  padding = true,
  shadow = false,
  transparent = false,
  title,
}: PanelProps) {
  const classes = useStyles({
    centre,
    disabled,
    fullSize,
    hasTitle: !!title,
    light,
    padding,
    shadow,
    transparent,
  });

  return (
    <Card className={classes.root}>
      <div className={classes.contentWrapper}>
        {title && (
          <CardHeader
            className={classes.header}
            avatar={icon && <div className={classes.iconWrapper}>{icon}</div>}
            title={title}
            titleTypographyProps={{
              className: classes.title,
              variant: 'body1',
            }}
          />
        )}

        <CardContent className={classes.content}>{children}</CardContent>
      </div>
    </Card>
  );
}

export default Panel;
